import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  useStaffAuth
} from "../context/StaffAuthContext";
import {
  createPeriod,
  updatePeriod,
  deletePeriod,
  getTimetableForSection
} from "../api/timetable/timetableApi";
import {
  createPeriodPortal,
  updatePeriodPortal,
  deletePeriodPortal,
  getTimetableForSectionPortal
} from "../api/timetable/timetablePortalApi";
import {
  getAllSubjects
} from "../api/subjects/subjectApi";
import TimetableGrid from "../components/timetable/TimetableGrid";
import OngoingClassesPanel from "../components/timetable/OngoingClassesPanel";
import VacantPeriodsPanel from "../components/timetable/VacantPeriodsPanel";
import axios from "axios";
import {
  Calendar,
  Settings,
  Search
} from "lucide-react";
import {
  Button,
  Input,
  Select,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  Modal,
  EmptyState,
  Skeleton
} from "../components/ui";
import {
  useToast
} from "../context/ToastContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";
const axiosInstance = axios.create({ baseURL: API, withCredentials: true });

const DAY_OPTIONS = [
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
];

const PERIOD_TYPE_OPTIONS = [
  { label: "Class", value: "CLASS" },
  { label: "Lab", value: "LAB" },
  { label: "Break", value: "BREAK" },
  { label: "Assembly", value: "ASSEMBLY" },
  { label: "Free Period", value: "FREE" },
  { label: "Activity", value: "ACTIVITY" },
];

export default function TimetableManager({ isPortal = false }) {
  const { hasPermission } = useStaffAuth();
  const canManage = isPortal ? hasPermission("TIMETABLE_FULL") : true;

  const [classList, setClassList] = useState([]);
  const [sectionList, setSectionList] = useState([]);
  const [subjectList, setSubjectList] = useState([]);
  const [staffList, setStaffList] = useState([]);

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");

  const [timetableData, setTimetableData] = useState({});

  const [loading, setLoading] = useState(false);
  const toast = useToast();

  // Periods-per-day dynamic DB state + Loading status
  const [periodsPerDay, setPeriodsPerDay] = useState(null);
  const [periodsInput, setPeriodsInput] = useState("");
  const [isSettingsLoading, setIsSettingsLoading] = useState(true);
  const [savingPeriods, setSavingPeriods] = useState(false);

  // Modal State
  const [modal, setModal] = useState(false);
  const [editingPeriodId, setEditingPeriodId] = useState(null);
  const [formData, setFormData] = useState({
    dayOfWeek: 1,
    periodNumber: 1,
    startTime: "09:00",
    endTime: "09:45",
    periodType: "CLASS",
    subjectId: "",
    staffId: "",
  });

  const showSuccess = (msg) => {
    toast.success(msg);
  };
  const showError = (msg) => {
    toast.error(msg);
  };

  const fetchDropdowns = async () => {
    try {
      const [classRes, subRes, staffRes] = await Promise.all([
        axiosInstance.get("/classes/all").catch(() => ({ data: [] })),
        getAllSubjects().catch(() => []),
        axiosInstance.get("/staff/all").catch(() => ({ data: [] })),
      ]);

      setClassList(Array.isArray(classRes.data) ? classRes.data : classRes.data?.classes || []);
      setSubjectList(Array.isArray(subRes) ? subRes : subRes?.data || []);
      setStaffList(Array.isArray(staffRes.data) ? staffRes.data : staffRes.data?.staff || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPeriodsPerDay = useCallback(async () => {
    setIsSettingsLoading(true);
    try {
      const url = isPortal ? "/timetable-settings-portal" : "/timetable-settings";
      const res = await axiosInstance.get(url);
      const value = res.data?.periodsPerDay;
      if (value && !isNaN(Number(value))) {
        const numVal = Number(value);
        setPeriodsPerDay(numVal);
        setPeriodsInput(numVal);
      } else {
        setPeriodsPerDay(null);
        setPeriodsInput("");
      }
    } catch (err) {
      setPeriodsPerDay(null);
      setPeriodsInput("");
    } finally {
      setIsSettingsLoading(false);
    }
  }, [isPortal]);

  const handleLoadTimetable = useCallback(async () => {
    if (!selectedSection) {
      showError("Please select class and section first");
      return;
    }
    setLoading(true);
    try {
      let res;
      if (isPortal) {
        res = await getTimetableForSectionPortal(selectedSection);
      } else {
        res = await getTimetableForSection(selectedSection);
      }

      const raw = Array.isArray(res) ? res : res.data || res.timetable || [];
      let grouped = {};
      if (Array.isArray(raw)) {
        grouped = raw.reduce((acc, period) => {
          const day = period.dayOfWeek;
          if (!acc[day]) acc[day] = [];
          acc[day].push(period);
          return acc;
        }, {});
      } else {
        grouped = raw;
      }

      setTimetableData(grouped);
    } catch (err) {
      showError(err.response?.data?.message || "Failed to load timetable");
    } finally {
      setLoading(false);
    }
  }, [isPortal, selectedSection]);

  useEffect(() => {
    fetchDropdowns();
    fetchPeriodsPerDay();
  }, [fetchPeriodsPerDay]);

  useEffect(() => {
    if (selectedSection) {
      handleLoadTimetable();
    }
  }, [selectedSection, handleLoadTimetable]);

  const handleClassChange = async (classId) => {
    setSelectedClass(classId);
    setSelectedSection("");
    setTimetableData({});
    if (!classId) {
      setSectionList([]);
      return;
    }
    try {
      const res = await axiosInstance.get("/sections/all", { params: { classId } });
      setSectionList(Array.isArray(res.data) ? res.data : res.data?.sections || []);
    } catch (err) {
      setSectionList([]);
    }
  };

  const handleOpenAdd = (dayOfWeek, periodNumber) => {
    setEditingPeriodId(null);
    setFormData({
      dayOfWeek,
      periodNumber,
      startTime: "09:00",
      endTime: "09:45",
      periodType: "CLASS",
      subjectId: "",
      staffId: "",
    });
    setModal(true);
  };

  const handleOpenEdit = (period) => {
    setEditingPeriodId(period._id);
    setFormData({
      dayOfWeek: period.dayOfWeek,
      periodNumber: period.periodNumber,
      startTime: period.startTime,
      endTime: period.endTime,
      periodType: period.periodType,
      subjectId: period.subjectId?._id || "",
      staffId: period.staffId?._id || "",
    });
    setModal(true);
  };

  const handleSubmitPeriod = async (e) => {
    e.preventDefault();
    if (!selectedClass || !selectedSection) {
      showError("Missing class or section context");
      return;
    }

    const payload = {
      ...formData,
      classId: selectedClass,
      sectionId: selectedSection,
    };

    try {
      if (editingPeriodId) {
        if (isPortal) await updatePeriodPortal(editingPeriodId, payload);
        else await updatePeriod(editingPeriodId, payload);
        showSuccess("Period updated");
      } else {
        if (isPortal) await createPeriodPortal(payload);
        else await createPeriod(payload);
        showSuccess("Period created");
      }
      setModal(false);
      handleLoadTimetable();
    } catch (err) {
      showError(err.response?.data?.message || "Operation failed");
    }
  };

  const handleDeletePeriod = async (id) => {
    if (!window.confirm("Delete this period?")) return;
    try {
      if (isPortal) await deletePeriodPortal(id);
      else await deletePeriod(id);
      showSuccess("Period deleted");
      handleLoadTimetable();
    } catch (err) {
      showError(err.response?.data?.message || "Failed to delete period");
    }
  };

  const handleSavePeriodsPerDay = async () => {
    const value = Number(periodsInput);
    if (!Number.isInteger(value) || value < 1 || value > 20) {
      showError("Periods per day must be a whole number between 1 and 20");
      return;
    }
    setSavingPeriods(true);
    try {
      await axiosInstance.put("/timetable-settings", { periodsPerDay: value });
      setPeriodsPerDay(value);
      showSuccess("Periods per day updated");
    } catch (err) {
      showError(err.response?.data?.message || "Failed to update periods per day");
    } finally {
      setSavingPeriods(false);
    }
  };

  const classOptions = useMemo(
    () => [
      { label: "-- Select Class --", value: "" },
      ...classList.map((c) => ({ label: c.className, value: c._id })),
    ],
    [classList]
  );

  const sectionOptions = useMemo(
    () => [
      { label: "-- Select Section --", value: "" },
      ...sectionList.map((sec) => ({ label: sec.sectionName, value: sec._id })),
    ],
    [sectionList]
  );

  const subjectOptions = useMemo(
    () => [
      { label: "-- Choose Subject --", value: "" },
      ...subjectList.map((s) => ({ label: `${s.name} (${s.code})`, value: s._id })),
    ],
    [subjectList]
  );

  const staffOptions = useMemo(
    () => [
      { label: "-- Choose Teacher --", value: "" },
      ...staffList.map((st) => ({ label: `${st.name} (${st.staffCode})`, value: st._id })),
    ],
    [staffList]
  );

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 antialiased">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
          <Calendar className="h-7 w-7 text-primary-900 dark:text-primary-300" />
          Timetable Manager
        </h1>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">Design and manage weekly class schedules.</p>
      </div>

      
      

      {/* Settings Bar */}
      {!isPortal && (
        <Card>
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <Settings className="h-4 w-4 text-neutral-400 dark:text-neutral-500 shrink-0" />
            <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
              Periods per day
            </label>
            <div className="w-28">
              <Input
                type="number"
                min="1"
                max="20"
                placeholder="e.g. 8"
                value={periodsInput}
                onChange={(e) => setPeriodsInput(e.target.value)}
                disabled={isSettingsLoading}
              />
            </div>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleSavePeriodsPerDay}
              isLoading={savingPeriods}
              disabled={isSettingsLoading}
            >
              Save
            </Button>
            {isSettingsLoading ? (
              <span className="text-xs text-neutral-400 dark:text-neutral-500 font-medium animate-pulse">
                Loading settings...
              </span>
            ) : !periodsPerDay ? (
              <span className="text-xs text-warning-700 font-semibold">
                ⚠️ please define no. of classes/period
              </span>
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* Step 1: Selection Filter Card */}
      <Card>
        <CardContent className="p-4 sm:p-5 flex flex-col md:flex-row items-end gap-4">
          <div className="flex-1 w-full">
            <Select
              label="Select Class"
              options={classOptions}
              value={selectedClass}
              onChange={(val) => handleClassChange(val)}
            />
          </div>

          <div className="flex-1 w-full">
            <Select
              label="Select Section"
              options={sectionOptions}
              value={selectedSection}
              disabled={!selectedClass}
              onChange={(val) => setSelectedSection(val)}
            />
          </div>

          <Button
            type="button"
            variant="primary"
            onClick={handleLoadTimetable}
            className="w-full md:w-auto shrink-0"
            leftIcon={<Search className="h-4 w-4" />}
          >
            Load Timetable
          </Button>
        </CardContent>
      </Card>

      {/* Step 2: Timetable Grid */}
      {loading || isSettingsLoading ? (
        <Card>
          <CardContent className="p-8 text-center space-y-3">
            <Skeleton height={24} width="30%" className="mx-auto" />
            <Skeleton height={200} width="100%" />
          </CardContent>
        </Card>
      ) : (
        <TimetableGrid
          timetable={timetableData}
          canManage={canManage && !!selectedSection}
          onEditPeriod={handleOpenEdit}
          onDeletePeriod={handleDeletePeriod}
          onAddPeriod={handleOpenAdd}
          periodsPerDay={periodsPerDay}
        />
      )}

      {/* Live panels — auto HTTP load + Socket.IO timetable:ongoing / timetable:vacant */}
      <OngoingClassesPanel isPortal={isPortal} autoFetch />
      <VacantPeriodsPanel isPortal={isPortal} autoFetch />

      {/* Period Create/Edit Modal */}
      <Modal
        isOpen={modal}
        onClose={() => setModal(false)}
        title={editingPeriodId ? "Edit Period" : "Add Period"}
        footer={
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 w-full">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setModal(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="periodForm"
              variant="primary"
              className="w-full sm:w-auto"
            >
              Save Period
            </Button>
          </div>
        }
      >
        <form id="periodForm" onSubmit={handleSubmitPeriod} className="space-y-4 pt-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select
              label="Day of Week"
              options={DAY_OPTIONS}
              value={formData.dayOfWeek}
              onChange={(val) => setFormData({ ...formData, dayOfWeek: Number(val) })}
            />
            <Input
              type="number"
              label="Period Number"
              min="1"
              max={periodsPerDay || 20}
              value={formData.periodNumber}
              onChange={(e) => setFormData({ ...formData, periodNumber: Number(e.target.value) })}
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              type="time"
              label="Start Time"
              value={formData.startTime}
              onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
              required
            />
            <Input
              type="time"
              label="End Time"
              value={formData.endTime}
              onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
              required
            />
          </div>

          <Select
            label="Period Type"
            options={PERIOD_TYPE_OPTIONS}
            value={formData.periodType}
            onChange={(val) => setFormData({ ...formData, periodType: val })}
          />

          {!["BREAK", "ASSEMBLY", "FREE"].includes(formData.periodType) && (
            <>
              <Select
                label="Subject"
                options={subjectOptions}
                value={formData.subjectId}
                onChange={(val) => setFormData({ ...formData, subjectId: val })}
                required
              />
              <Select
                label="Teacher"
                options={staffOptions}
                value={formData.staffId}
                onChange={(val) => setFormData({ ...formData, staffId: val })}
                required
              />
            </>
          )}
        </form>
      </Modal>
    </div>
  );
}